import React from 'react';
import styled from 'styled-components';
import { Tooltip, token } from '@bynder/design-system';
import { useNavigableItem } from '../KeyboardNavigation';

type Props = {
	children: React.ReactNode;
	icon?: React.ReactNode;
	isSelected: boolean;
	isDisabled: boolean;
	tooltip?: string;
	onClick?: () => void;
};

export function CategoryChip({ children, icon, isSelected, isDisabled, tooltip, onClick }: Props) {
	const { isActive, itemRef } = useNavigableItem<HTMLButtonElement>(() => onClick?.(), {
		afterSelect: 'none',
	});

	const chip = (
		<ChipStyled
			ref={isDisabled ? undefined : itemRef}
			onClick={onClick}
			disabled={isDisabled}
			$isSelected={isSelected}
			$isActive={!isDisabled && isActive}
		>
			{icon && <IconStyled data-testid="categoryChipIcon">{icon}</IconStyled>}
			<StyledWrapper>{children}</StyledWrapper>
		</ChipStyled>
	);

	return tooltip ? <Tooltip content={tooltip}>{chip}</Tooltip> : chip;
}

const ChipStyled = styled.button<{ $isSelected: boolean; $isActive?: boolean }>`
	display: inline-flex;
	align-items: center;
	height: 32px;
	gap: ${token.spacing2};
	padding: 0 ${token.spacing3};
	border: 0;
	border-radius: ${token.radiusPill};
	font-size: 14px;
	white-space: nowrap;
	vertical-align: top;
	cursor: pointer;
	outline: none;
	background-color: ${({ $isSelected, $isActive }) => {
		if ($isSelected) {
			return $isActive ? token.blue100 : token.blue50;
		}

		return $isActive ? token.gray75 : token.gray50;
	}};
	color: ${({ $isSelected }) => ($isSelected ? token.blue500 : token.colorText)};

	&:disabled {
		pointer-events: none;
		background-color: ${token.gray25};
		color: ${token.colorTextDisabled};
	}
`;

const IconStyled = styled.span`
	color: ${token.colorTextDisabled};
`;

const StyledWrapper = styled.div`
	padding: 0 ${token.spacing2};
`;
